import { useEffect } from 'react'
import { MultiSelectDropdown } from '../../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { redtailSettingsDefaultValues, RedtailSettingsFormValues } from './model'
import { Controller, useForm } from 'react-hook-form'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { Button } from '../../../../../components/Button/Button'
import { useRedtailContactSync } from './hooks'

export const RedtailContactSyncSettings = () => {
  const {
    checkpoint,
    isGetCheckpointFetching,
    userData,
    isSyncDisabled,
    isAccountFetching,
    isGetMeQueryFetching,
    contactCategoryOptions,
    contactStatusOptions,
    contactTypeOptions,
    tierCategoriesSelectOptions,
    isFetching,
    isSaving,
    isContactStatusesFetching,
    isContactTypesFetching,
    isContactCategoriesFetching,
    save,
  } = useRedtailContactSync()

  const form = useForm<RedtailSettingsFormValues>({ defaultValues: redtailSettingsDefaultValues })

  useEffect(() => {
    if (isGetCheckpointFetching || !checkpoint) {
      return
    }
    form.setValue('inbound.contact.autoSync', checkpoint.data?.active || false)
  }, [isGetCheckpointFetching, checkpoint, form])

  useEffect(() => {
    if (isGetMeQueryFetching || !userData) {
      return
    }
    const integration = userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)
    if (!integration) {
      return
    }
    if (integration.serviceConfig?.inbound?.contact?.filters) {
      form.setValue('inbound.contact.filters', integration.serviceConfig.inbound.contact.filters)
    }
    if (integration.serviceConfig?.inbound?.contact?.tierCategories) {
      form.setValue('inbound.contact.tierCategories', integration.serviceConfig.inbound.contact.tierCategories)
    }
  }, [isGetMeQueryFetching, userData, form])

  const onSubmit = async (data: RedtailSettingsFormValues) => {
    await save(data)
  }

  const handleCancel = () => {
    form.reset()
  }

  if (isFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and Redtail.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch
                    checked={field.value === true}
                    onChange={field.onChange}
                    disabled={isGetCheckpointFetching || isSyncDisabled || isAccountFetching}
                  />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <hr></hr>
      <div className={'label-large'}>Filters</div>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Status</div>
        </Col>
        <Col>
          {contactStatusOptions ? (
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Controller
                name='inbound.contact.filters.status_id'
                control={form.control}
                render={({ field }) => (
                  <MultiSelectDropdown
                    name={field.name}
                    options={contactStatusOptions}
                    onSelectedChanged={field.onChange}
                    initialSelectedOptions={field.value ?? []}
                  />
                )}
              />
            </ScopedBlock>
          ) : null}
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Type</div>
        </Col>
        <Col>
          {contactTypeOptions ? (
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Controller
                name='inbound.contact.filters.type'
                control={form.control}
                render={({ field }) => (
                  <MultiSelectDropdown
                    name={field.name}
                    options={contactTypeOptions}
                    onSelectedChanged={field.onChange}
                    initialSelectedOptions={field.value ?? []}
                  />
                )}
              />
            </ScopedBlock>
          ) : null}
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Category</div>
        </Col>
        <Col>
          {contactCategoryOptions ? (
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Controller
                name='inbound.contact.filters.category_id'
                control={form.control}
                render={({ field }) => (
                  <MultiSelectDropdown
                    name={field.name}
                    options={contactCategoryOptions}
                    onSelectedChanged={field.onChange}
                    initialSelectedOptions={field.value ?? []}
                  />
                )}
              />
            </ScopedBlock>
          ) : null}
        </Col>
      </Row>
      <hr></hr>
      <div className={'label-large'}>Tiers</div>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Categories</div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.tierCategories'}
              control={form.control}
              render={({ field }) => (
                <MultiSelectDropdown
                  name={field.name}
                  options={tierCategoriesSelectOptions ?? []}
                  initialSelectedOptions={field.value ?? []}
                  onSelectedChanged={field.onChange}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isSaving}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button
                variant='primary'
                onClick={form.handleSubmit(onSubmit)}
                loading={isSaving}
                disabled={isContactStatusesFetching || isContactTypesFetching || isContactCategoriesFetching}
              >
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
